import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const roles = [
  'member', 'secretary', 'treasurer',
  'costume_custodian', 'instrument_custodian',
  'president', 'vice_president', 'admin'
];

const Members = () => {
  const [members, setMembers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, activeTab, search, sortBy]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await API.get('/members');
      setMembers(res.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
    setLoading(false);
  };

  const filterMembers = () => {
    let result = [...members];

    // Tab filter
    if (activeTab === 'pending') {
      result = result.filter(m => m.status === 'pending');
    } else if (activeTab === 'approved') {
      result = result.filter(m => m.status === 'approved');
    } else if (activeTab === 'officers') {
      result = result.filter(m =>
        m.role !== 'member' && m.status === 'approved'
      );
    }

    // Search filter
    if (search) {
      result = result.filter(m =>
        m.first_name.toLowerCase().includes(search.toLowerCase()) ||
        m.last_name.toLowerCase().includes(search.toLowerCase()) ||
        m.school_id.toLowerCase().includes(search.toLowerCase()) ||
        m.email.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Sort by college
    if (sortBy !== 'all') {
      result = result.filter(m => m.college_abbr === sortBy);
    }

    setFiltered(result);
  };

  const handleApprove = async (id) => {
    try {
      await API.put(`/members/${id}/approve`);
      setMessage('✅ Member approved!');
      fetchMembers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Error approving member!');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Are you sure you want to reject this member?')) return;
    try {
      await API.put(`/members/${id}/reject`);
      setMessage('✅ Member rejected!');
      fetchMembers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Error rejecting member!');
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Are you sure you want to remove this member? This cannot be undone!')) return;
    try {
      await API.delete(`/members/${id}`);
      setMessage('✅ Member removed!');
      setSelectedMember(null);
      fetchMembers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ ' + (error.response?.data?.message || 'Error removing member!'));
    }
  };

  const handleChangeRole = async () => {
    if (!newRole) return;
    try {
      await API.put(`/members/${selectedMember.id}/role`, { role: newRole });
      setMessage('✅ Role updated!');
      setShowRoleModal(false);
      setSelectedMember(null);
      fetchMembers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Error updating role!');
    }
  };

  const colleges = [...new Set(members.map(m => m.college_abbr).filter(Boolean))];

  const pendingCount = members.filter(m => m.status === 'pending').length;

  if (loading) return (
    <div style={styles.loading}>
      <p>Loading members...</p>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>👥 Members</h2>
          <p style={styles.subtitle}>Manage all Dulimbay members</p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={message.includes('✅') ? styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: 'all', label: 'All Members' },
          { id: 'pending', label: `Pending (${pendingCount})` },
          { id: 'approved', label: 'Approved' },
          { id: 'officers', label: 'Officers' },
        ].map(tab => (
          <button
            key={tab.id}
            style={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Sort */}
      <div style={styles.filterRow}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="🔍 Search by name, ID, or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={styles.sortSelect}
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
        >
          <option value="all">All Colleges</option>
          {colleges.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Members Grid */}
      {filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyIcon}>👥</p>
          <p style={styles.emptyText}>No members found!</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {filtered.map(member => (
            <div
              key={member.id}
              style={styles.memberCard}
              onClick={() => setSelectedMember(member)}
            >
              {/* Avatar */}
              <div style={styles.avatarBox}>
                {member.profile_photo ? (
                  <img src={member.profile_photo} alt="profile"
                    style={styles.avatarImg} />
                ) : (
                  <div style={{
                    ...styles.avatar,
                    background: member.role === 'admin' ? '#e74c3c' :
                      member.role === 'member' ? '#3498db' : '#2ecc71'
                  }}>
                    {member.first_name?.charAt(0)}{member.last_name?.charAt(0)}
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={styles.memberInfo}>
                <p style={styles.memberName}>
                  {member.first_name} {member.last_name}
                </p>
                <p style={styles.memberId}>{member.school_id}</p>
                <p style={styles.memberCollege}>
                  {member.college_abbr || 'No college'}
                </p>
                <div style={styles.badgeRow}>
                  <span style={{
                    ...styles.roleBadge,
                    background: member.role === 'admin' ? '#e74c3c' :
                      member.role === 'member' ? '#3498db' : '#2ecc71'
                  }}>
                    {member.role?.replace('_', ' ')}
                  </span>
                  <span style={{
                    ...styles.statusBadge,
                    background: member.status === 'approved' ? '#2ecc71' :
                      member.status === 'pending' ? '#f39c12' : '#e74c3c'
                  }}>
                    {member.status}
                  </span>
                </div>
              </div>

              {/* Quick Actions for Pending */}
              {member.status === 'pending' && (
                <div style={styles.quickActions}
                  onClick={e => e.stopPropagation()}>
                  <button style={styles.approveBtn}
                    onClick={() => handleApprove(member.id)}>
                    ✓
                  </button>
                  <button style={styles.rejectBtn}
                    onClick={() => handleReject(member.id)}>
                    ✕
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Member Detail Modal */}
      {selectedMember && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div style={styles.modalAvatar}>
                {selectedMember.first_name?.charAt(0)}
                {selectedMember.last_name?.charAt(0)}
              </div>
              <div>
                <h3 style={styles.modalName}>
                  {selectedMember.first_name} {selectedMember.middle_name} {selectedMember.last_name}
                </h3>
                <p style={styles.modalId}>{selectedMember.school_id}</p>
                <div style={styles.badgeRow}>
                  <span style={{
                    ...styles.roleBadge,
                    background: selectedMember.role === 'admin' ? '#e74c3c' :
                      selectedMember.role === 'member' ? '#3498db' : '#2ecc71'
                  }}>
                    {selectedMember.role?.replace('_', ' ')}
                  </span>
                  <span style={{
                    ...styles.statusBadge,
                    background: selectedMember.status === 'approved' ? '#2ecc71' :
                      selectedMember.status === 'pending' ? '#f39c12' : '#e74c3c'
                  }}>
                    {selectedMember.status}
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Email</p>
                  <p style={styles.infoValue}>{selectedMember.email}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Phone</p>
                  <p style={styles.infoValue}>{selectedMember.phone || 'N/A'}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Facebook</p>
                  <p style={styles.infoValue}>{selectedMember.facebook || 'N/A'}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Gender</p>
                  <p style={styles.infoValue}>{selectedMember.gender}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Member Type</p>
                  <p style={styles.infoValue}>{selectedMember.member_type || 'student'}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>College</p>
                  <p style={styles.infoValue}>{selectedMember.college_name || 'N/A'}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Department</p>
                  <p style={styles.infoValue}>{selectedMember.department_name || 'N/A'}</p>
                </div>
                <div style={styles.infoItem}>
                  <p style={styles.infoLabel}>Joined</p>
                  <p style={styles.infoValue}>
                    {new Date(selectedMember.created_at).toLocaleDateString('en-PH')}
                  </p>
                </div>
              </div>

              <div style={styles.emergencyBox}>
                <p style={styles.emergencyTitle}>🆘 Emergency Contact</p>
                <p style={styles.infoValue}>
                  {selectedMember.emergency_contact_name} ({selectedMember.emergency_contact_relationship})
                </p>
                <p style={styles.infoValue}>{selectedMember.emergency_contact_phone}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={styles.actionButtons}>
              {selectedMember.status === 'pending' && (
                <>
                  <button style={styles.approveBtnFull}
                    onClick={() => { handleApprove(selectedMember.id); setSelectedMember(null); }}>
                    ✓ Approve
                  </button>
                  <button style={styles.rejectBtnFull}
                    onClick={() => { handleReject(selectedMember.id); setSelectedMember(null); }}>
                    ✕ Reject
                  </button>
                </>
              )}
              {selectedMember.status === 'approved' && (
                <button style={styles.roleBtnFull}
                  onClick={() => { setNewRole(selectedMember.role); setShowRoleModal(true); }}>
                  🔄 Change Role
                </button>
              )}
              {selectedMember.role !== 'admin' && (
                <button style={styles.removeBtnFull}
                  onClick={() => handleRemove(selectedMember.id)}>
                  🗑️ Remove
                </button>
              )}
              <button style={styles.closeBtnFull}
                onClick={() => setSelectedMember(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: '360px' }}>
            <h3 style={styles.modalTitle}>🔄 Change Role</h3>
            <p style={styles.modalSub}>
              {selectedMember?.first_name} {selectedMember?.last_name}
            </p>
            <select
              style={styles.roleSelect}
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
            >
              {roles.map(r => (
                <option key={r} value={r}>
                  {r.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowRoleModal(false)}>
                Cancel
              </button>
              <button style={styles.doneBtn} onClick={handleChangeRole}>
                Save Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  loading: { textAlign: 'center', padding: '50px', color: '#888' },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 18px',
    background: 'white',
    border: '1.5px solid #ddd',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#666',
  },
  tabActive: {
    padding: '8px 18px',
    background: '#0f3460',
    border: '1.5px solid #0f3460',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'white',
    fontWeight: 'bold',
  },
  filterRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    minWidth: '200px',
    padding: '10px 15px',
    borderRadius: '10px',
    border: '1.5px solid #ddd',
    fontSize: '14px',
    outline: 'none',
  },
  sortSelect: {
    padding: '10px 15px',
    borderRadius: '10px',
    border: '1.5px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    background: 'white',
  },
  emptyState: { textAlign: 'center', padding: '50px' },
  emptyIcon: { fontSize: '50px', margin: '0 0 10px 0' },
  emptyText: { color: '#888', fontSize: '16px' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '15px',
  },
  memberCard: {
    background: 'white',
    borderRadius: '14px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    position: 'relative',
    transition: 'transform 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  avatarBox: { marginBottom: '12px' },
  avatar: {
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '22px',
  },
  avatarImg: {
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  memberInfo: { width: '100%' },
  memberName: {
    margin: '0 0 4px 0',
    fontWeight: 'bold',
    fontSize: '14px',
    color: '#333',
  },
  memberId: { margin: '0 0 3px 0', color: '#888', fontSize: '12px' },
  memberCollege: { margin: '0 0 8px 0', color: '#aaa', fontSize: '12px' },
  badgeRow: {
    display: 'flex',
    gap: '5px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  roleBadge: {
    padding: '3px 10px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  statusBadge: {
    padding: '3px 10px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  quickActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
  approveBtn: {
    flex: 1,
    padding: '6px',
    background: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  rejectBtn: {
    flex: 1,
    padding: '6px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 1001,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    padding: '30px',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #eee',
  },
  modalAvatar: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: '#0f3460',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '20px',
    flexShrink: 0,
  },
  modalName: { margin: '0 0 4px 0', fontSize: '18px', color: '#333' },
  modalId: { margin: '0 0 8px 0', color: '#888', fontSize: '13px' },
  modalBody: { marginBottom: '20px' },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '15px',
  },
  infoItem: {
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '10px',
  },
  infoLabel: {
    margin: '0 0 4px 0',
    fontSize: '11px',
    color: '#aaa',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  infoValue: { margin: 0, fontSize: '13px', color: '#333' },
  emergencyBox: {
    background: '#fff3f3',
    borderRadius: '8px',
    padding: '12px',
    borderLeft: '4px solid #e74c3c',
  },
  emergencyTitle: {
    margin: '0 0 8px 0',
    fontWeight: 'bold',
    color: '#e74c3c',
    fontSize: '13px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  approveBtnFull: {
    flex: 1,
    padding: '11px',
    background: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
  },
  rejectBtnFull: {
    flex: 1,
    padding: '11px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
  },
  roleBtnFull: {
    flex: 1,
    padding: '11px',
    background: '#9b59b6',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
  },
  removeBtnFull: {
    flex: 1,
    padding: '11px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
  },
  closeBtnFull: {
    flex: 1,
    padding: '11px',
    background: 'white',
    color: '#666',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
  },
  modalTitle: { margin: '0 0 8px 0', fontSize: '18px', color: '#333' },
  modalSub: { margin: '0 0 15px 0', color: '#888', fontSize: '14px' },
  roleSelect: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: '1.5px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    marginBottom: '15px',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
  },
  cancelBtn: {
    flex: 1,
    padding: '12px',
    background: 'white',
    color: '#666',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  doneBtn: {
    flex: 1,
    padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
};

export default Members;